"use client";

import { useState, useEffect, useRef } from "react";
import Modal from "@/components/ui/modal/Modal";
import ProductCarousel from "@/components/productCarousel/ProductCarousel";
import Button from "@/components/ui/button/Button";
import { CLink } from "@/components/common/customLink/CustomLink";
import { TranslationData, PairEyewearTopsContent } from "@/common/lib/types";
import { RecommendProductItem } from "@/apiUtils/algoliaUtils/types";
import { getLangFromLocale } from "@/utils/common";
import { getChannelId } from "@/apiUtils/commonUtils";
import { cn } from "@/lib/tailwind";
import { getCollectionById } from "@/api/saleor/services";
import { getProductDetailsFromAlgoliaBySlugs } from "@/apiUtils/algoliaUtils";
import { getAlgoliaIndexName } from "@/apiUtils/algoliaUtils";
import { recommendationType } from "@/apiUtils/constants";
import Skeleton from "@/components/ui/skeleton/Skeleton";
import { useCustomToast } from "@/hooks/useCustomToast";
import { buildPairEyewearTopsAddedMessage } from "@/utils/cart.utils";

interface PairEyewearModalProps {
  isOpen: boolean;
  onClose: () => void;
  pairEyewearTopsContent: PairEyewearTopsContent | null;
  translations: TranslationData;
  locale: string;
  enableVto?: boolean;
  missingTopModel?: Array<{ model: string }>;
  variantIdsToExcludeFromAddedCount?: string[];
  deferredFrameToast?: { imgUrl: string; text: string } | null;
}

const PairEyewearTopModal = ({
  isOpen,
  onClose,
  pairEyewearTopsContent,
  translations,
  locale,
  enableVto = false,
  missingTopModel,
  deferredFrameToast,
}: PairEyewearModalProps) => {
  const lang = getLangFromLocale(locale);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [productsByTab, setProductsByTab] = useState<Record<number, RecommendProductItem[]>>({});
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [deferredTopToastImages, setDeferredTopToastImages] = useState<string[]>([]);

  const { showToast } = useCustomToast();
  const fetchedTabIndicesRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!isOpen) {
      fetchedTabIndicesRef.current = new Set();
      setProductsByTab({});
      setDeferredTopToastImages([]);
    }
  }, [isOpen]);

  const handleModalClose = () => {
    if (deferredFrameToast?.imgUrl) {
      showToast({
        imgUrl: deferredFrameToast.imgUrl,
        text: deferredFrameToast.text,
        lang,
        redirectUrl: "/cart",
      });
    }

    if (deferredTopToastImages.length > 3) {
      const message = buildPairEyewearTopsAddedMessage(translations?.cart, deferredTopToastImages.length);
      if (message) {
        const w = message.split(" ");
        showToast({ text: <><strong>{w.slice(0, 2).join(" ")}</strong> {w.slice(2).join(" ")}</>, lang, hideImage: true });
      }
    } else {
      deferredTopToastImages.forEach((imgUrl) => {
        showToast({
          imgUrl,
          text: translations?.common?.common_added_to_cart_toast_message ?? "",
          lang,
          redirectUrl: "/cart",
        });
      });
    }

    onClose();
  };

  const allItems =
    pairEyewearTopsContent?.items && pairEyewearTopsContent.enable
      ? pairEyewearTopsContent.items
      : [];

  const validationModels = missingTopModel ?? [];
  const items =
    validationModels.length > 0
      ? allItems.filter((item) =>
          validationModels.some(
            (m) =>
              (m.model?.trim().toLowerCase() ?? "") ===
              ((item.slug || item.title || "").trim().toLowerCase() ?? ""),
          ),
        )
      : allItems;

  const selectedItem = items[selectedTabIndex];

  useEffect(() => {
    setSelectedTabIndex((prev) => (prev >= items.length ? 0 : prev));
  }, [items.length]);

  useEffect(() => {
    const fetchProducts = async () => {
      if (!selectedItem?.collection?.id || !isOpen) {
        return;
      }
      if (fetchedTabIndicesRef.current.has(selectedTabIndex)) {
        return;
      }
      fetchedTabIndicesRef.current.add(selectedTabIndex);

      setLoadingProducts(true);
      try {
        const channel = getChannelId(locale);
        const collection = await getCollectionById(selectedItem.collection.id, channel);

        if (collection?.slug) {
          const indexName = getAlgoliaIndexName("", locale);
          const response = await getProductDetailsFromAlgoliaBySlugs(
            [collection.slug],
            indexName,
            recommendationType.SALEOR_COLLECTION,
            [],
            10,
            lang,
          );

          const products =
            response?.[0]?.hits?.map((product) => ({
              ...product,
              queryID: response[0]?.queryID ?? "",
              indexName,
            })) || [];

          setProductsByTab((prev) => ({
            ...prev,
            [selectedTabIndex]: products,
          }));
        } else {
          setProductsByTab((prev) => ({
            ...prev,
            [selectedTabIndex]: [],
          }));
        }
      } catch (error) {
        console.error("Error fetching products for tab:", error);
        setProductsByTab((prev) => ({
          ...prev,
          [selectedTabIndex]: [],
        }));
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchProducts();
  }, [selectedItem?.collection?.id, locale, isOpen, selectedTabIndex, lang]);

  const displayProducts = productsByTab[selectedTabIndex] || [];
  const hasItems = items.length > 0;
  const hasTops = hasItems && (loadingProducts || displayProducts.length > 0);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      title={translations?.pairEyewear?.pair_eyewear_modal_header}
    >
      <div className="flex flex-col h-[calc(100vh-12rem)] max-h-[calc(100vh-12rem)] sm:w-[30rem] lg:w-[56rem]">
        {hasItems && (
          <h2 className="md:w-[54%] w-[82%] mx-auto text-center mb-6 flex-shrink-0">
            {translations?.pairEyewear?.pair_eyewear_modal_body}
          </h2>
        )}

        {hasItems && items.length > 1 && (
          <div className="mb-6 flex-shrink-0 inline-flex mx-auto">
            <div className="flex items-center gap-1">
              {items.map((item, index) => (
                <button
                  key={item.key}
                  onClick={() => setSelectedTabIndex(index)}
                  className={cn(
                    "py-3 px-6 text-sm text-center transition-all whitespace-nowrap",
                    selectedTabIndex === index
                      ? "bg-white font-bold text-black border-b-4 border-t border-l border-r border-gray-200 border-b-orange-500"
                      : "bg-gray-100 font-normal text-black border-b-2 border-transparent hover:bg-gray-50",
                  )}
                >
                  {item.title || `Tops ${index + 1}`}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar flex flex-col">
          {!hasTops ? (
            <div className="flex-1 flex items-center justify-center min-h-full">
              <p className="text-center text-sm text-gray-500">
                {translations?.pairEyewear?.pair_eyewear_no_tops_available_message ?? translations?.pdp?.pdp_pair_eyewear_no_tops_available_message ?? "No tops available"}
              </p>
            </div>
          ) : loadingProducts ? (
            <div className="flex-1 flex gap-4 pb-4 min-h-full items-start">
              {[...Array(4)].map((_, idx) => (
                <Skeleton
                  key={idx}
                  className="h-[18rem] w-[184px] flex-shrink-0 bg-tertiary-5 md:h-[20rem] md:w-[251px]"
                />
              ))}
            </div>
          ) : (
            <ProductCarousel
              translations={translations}
              tryOn={enableVto}
              products={displayProducts}
              title=""
              parentContainer={false}
              showCartAddedState={true}
              isChevronVariant={false}
              onDeferProductToast={(imgUrl) => setDeferredTopToastImages((prev) => [...prev, imgUrl])}
            />
          )}

          {hasTops && selectedItem?.collection?.url && !loadingProducts && (
            <div className="flex justify-center pt-4 pb-4 md:pt-2">
              <CLink href={selectedItem.collection.url}>
                <Button className="flex h-9 w-[140px] items-center justify-center !py-0 text-xs md:h-[50px] md:w-[252px] md:text-sm">
                  {translations?.pairEyewear?.pair_eyewear_modal_cta}
                </Button>
              </CLink>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PairEyewearTopModal;
